import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'

export default function LoadingScreen({ onComplete }) {
  const [phase, setPhase] = useState(0)

  useEffect(() => {
    const timings = [
      [1,   80],
      [2,  250],
      [3,  500],
      [4,  900],
      [5, 1300],
      [6, 2000],
    ]
    const timers = timings.map(([p, t]) =>
      setTimeout(() => setPhase(p), t)
    )
    return () => timers.forEach(clearTimeout)
  }, [])

  const name = 'ROHAN DALVI'

  return (
    <AnimatePresence onExitComplete={onComplete}>
      {phase < 6 && (
        <motion.div
          key="loader"
          initial={{ opacity: 1 }}
          exit={{
            opacity: 0,
            transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] }
          }}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 99999,
            background: '#020407',
            overflow: 'hidden',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 1 ? 1 : 0 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              inset: '-10%',
              backgroundImage: `
                linear-gradient(rgba(0,217,255,0.07) 1px, transparent 1px),
                linear-gradient(90deg, rgba(0,217,255,0.07) 1px, transparent 1px)
              `,
              backgroundSize: '60px 60px',
              backgroundPosition: 'center center'
            }}
          />

          <motion.div
            initial={{ opacity: 0, scaleY: 0 }}
            animate={{ 
              opacity: phase >= 1 ? 0.6 : 0,
              scaleY: phase >= 1 ? 1 : 0
            }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'absolute',
              bottom: 0,
              left: '-50%',
              right: '-50%',
              height: '55%',
              background: `
                repeating-linear-gradient(
                  90deg,
                  transparent,
                  transparent calc(10% - 1px),
                  rgba(0,217,255,0.12) calc(10% - 1px),
                  rgba(0,217,255,0.12) 10%
                )
              `,
              transformOrigin: 'bottom center',
              transform: 'perspective(400px) rotateX(40deg)',
              maskImage: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)',
              WebkitMaskImage: 'linear-gradient(to top, rgba(0,0,0,0.8) 0%, transparent 100%)'
            }}
          />

          <motion.div
            initial={{ opacity: 0, scale: 0.3 }}
            animate={{
              opacity: phase >= 2 ? [0, 0.8, 0.3] : 0,
              scale: phase >= 2 ? [0.3, 1.8, 1.2] : 0.3
            }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            style={{
              position: 'absolute',
              width: '600px',
              height: '600px',
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(0,217,255,0.15) 0%, rgba(208,0,255,0.08) 40%, transparent 70%)',
              filter: 'blur(40px)',
              pointerEvents: 'none'
            }}
          />

          <motion.div
            initial={{ top: '-2px', opacity: 0 }}
            animate={{
              top: phase >= 2 ? ['0%', '100%'] : '-2px',
              opacity: phase >= 2 ? [0, 1, 1, 0] : 0
            }}
            transition={{ duration: 0.4, ease: 'easeInOut', delay: 0.05 }}
            style={{
              position: 'absolute',
              left: 0,
              right: 0,
              height: '1px',
              background: 'linear-gradient(90deg, transparent, #00d9ff, transparent)',
              boxShadow: '0 0 12px #00d9ff',
              zIndex: 3
            }}
          />

          {phase >= 2 && ['tl','tr','bl','br'].map((corner, i) => (
            <motion.div
              key={corner}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: i * 0.04, duration: 0.2, ease: 'easeOut' }}
              style={{
                position: 'absolute',
                width: '30px',
                height: '30px',
                ...(corner.includes('t') ? { top: '2rem' } : { bottom: '2rem' }),
                ...(corner.includes('l') ? { left: '2rem' } : { right: '2rem' }),
                borderTop: corner.includes('t') ? '1px solid rgba(0,217,255,0.5)' : 'none',
                borderBottom: corner.includes('b') ? '1px solid rgba(0,217,255,0.5)' : 'none',
                borderLeft: corner.includes('l') ? '1px solid rgba(0,217,255,0.5)' : 'none',
                borderRight: corner.includes('r') ? '1px solid rgba(0,217,255,0.5)' : 'none',
              }}
            />
          ))}

          <div style={{
            position: 'relative',
            zIndex: 4,
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1.2rem'
          }}>

            <motion.div
              initial={{ opacity: 0, letterSpacing: '0.8em' }}
              animate={{
                opacity: phase >= 3 ? 1 : 0,
                letterSpacing: phase >= 3 ? '0.35em' : '0.8em'
              }}
              transition={{ duration: 0.35, ease: 'easeOut' }}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.65rem',
                color: 'rgba(0,217,255,0.6)',
                textTransform: 'uppercase'
              }}
            >
              PORTFOLIO // 2026
            </motion.div>

            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'center',
              gap: '0 0.01em',
              overflow: 'hidden'
            }}>
              {name.split('').map((char, i) => (
                <div
                  key={i}
                  style={{ overflow: 'hidden', display: 'inline-block' }}
                >
                  <motion.span
                    initial={{ y: '105%' }}
                    animate={{ y: phase >= 3 ? '0%' : '105%' }}
                    transition={{
                      delay: i * 0.025,
                      duration: 0.3,
                      ease: [0.16, 1, 0.3, 1]
                    }}
                    style={{
                      display: 'inline-block',
                      fontSize: 'clamp(3.5rem, 11vw, 9rem)',
                      fontWeight: 900,
                      letterSpacing: '-0.02em',
                      lineHeight: 0.95,
                      color: char === ' ' ? 'transparent' : 'white',
                      textShadow: char !== ' ' ? '0 0 60px rgba(0,217,255,0.2)' : 'none',
                      width: char === ' ' ? '0.3em' : 'auto'
                    }}
                  >
                    {char === ' ' ? '\u00A0' : char}
                  </motion.span>
                </div>
              ))}
            </div>

            <motion.p
              initial={{ opacity: 0, y: 12 }}
              animate={{
                opacity: phase >= 4 ? 1 : 0,
                y: phase >= 4 ? 0 : 12
              }}
              transition={{ duration: 0.3, ease: 'easeOut' }}
              style={{
                fontFamily: 'monospace',
                fontSize: '0.72rem',
                letterSpacing: '0.28em',
                color: '#00d9ff',
                textTransform: 'uppercase',
                margin: 0
              }}
            >
              CREATIVE DEVELOPER_
            </motion.p>

            <motion.div
              initial={{ opacity: 0, width: '0px' }}
              animate={{
                opacity: phase >= 4 ? 1 : 0,
                width: phase >= 4 ? '200px' : '0px'
              }}
              transition={{ duration: 0.2, ease: 'easeOut' }}
              style={{
                height: '1px',
                background: 'rgba(255,255,255,0.08)',
                borderRadius: '1px',
                overflow: 'hidden',
                position: 'relative'
              }}
            >
              <motion.div
                initial={{ x: '-100%' }}
                animate={{ x: phase >= 4 ? '0%' : '-100%' }}
                transition={{
                  duration: 0.35,
                  ease: [0.16, 1, 0.3, 1],
                  delay: 0.05
                }}
                style={{
                  position: 'absolute',
                  inset: 0,
                  background: 'linear-gradient(90deg, #00d9ff, #d000ff)',
                  boxShadow: '0 0 10px #00d9ff'
                }}
              />
            </motion.div>

          </div>

          <motion.div
            initial={{ opacity: 0, x: -16 }}
            animate={{
              opacity: phase >= 4 ? 1 : 0,
              x: phase >= 4 ? 0 : -16
            }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
            style={{
              position: 'absolute',
              bottom: '2rem',
              left: '2rem',
              zIndex: 5,
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              letterSpacing: '0.15em',
              color: 'rgba(0,217,255,0.5)',
              textTransform: 'uppercase'
            }}
          >
            <motion.div
              animate={{ opacity: [1, 0.2, 1] }}
              transition={{ duration: 1.2, repeat: Infinity }}
              style={{
                width: '5px', height: '5px',
                borderRadius: '50%',
                background: '#00d9ff',
                boxShadow: '0 0 6px #00d9ff'
              }}
            />
            ROCK // AI ONLINE
          </motion.div>

          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: phase >= 3 ? 0.5 : 0 }}
            whileHover={{ opacity: 1 }}
            onClick={onComplete}
            style={{
              position: 'absolute',
              bottom: '2rem',
              right: '2rem',
              zIndex: 5,
              background: 'transparent',
              border: '1px solid rgba(255,255,255,0.15)',
              borderRadius: '4px',
              padding: '5px 14px',
              color: 'rgba(255,255,255,0.5)',
              fontFamily: 'monospace',
              fontSize: '0.6rem',
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              cursor: 'none'
            }}
          >
            SKIP →
          </motion.button>

        </motion.div>
      )}
    </AnimatePresence>
  )
}
